import { Check, ArrowUpRight, Building2, Landmark, FileCheck, Calculator, UtensilsCrossed, Store, Hotel, MapPin, Globe2, Package, ScrollText, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "../components/Reveal";
import SectionTitle from "../components/SectionTitle";

const EUROPATH_IMG = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg";

const EUROPATH_GALLERY = [
  { src: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=70", label: "International Students" },
  { src: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&w=1200&q=70", label: "Classroom Learning" },
  { src: "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=1200&q=70", label: "Spanish Language Institute" },
  { src: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=70", label: "Nursing Vocational Programs" },
  { src: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=1200&q=70", label: "Healthcare Training" },
  { src: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?auto=format&fit=crop&w=1200&q=70", label: "European Universities" },
];

const BUSINESS_ICONS = [Building2, FileCheck, Landmark, Calculator, UtensilsCrossed, Store, Hotel, MapPin, Handshake, Package, ScrollText, Globe2];

const BUSINESS_IMG = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1400&q=70";

const Services = () => {
  const { t } = useLang();

  return (
    <div data-testid="services-page" className="pt-32">
      {/* Header */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline={t.services.overline} title={t.services.title} sub={t.services.sub} />
        </div>
      </section>

      {/* EUROPATH (Primary, large feature) */}
      <section className="bg-[#0A192F] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{t.services.primary}</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">{t.divisions.europath.name}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-3 text-sm uppercase tracking-[0.2em] text-[#D4AF37]">{t.divisions.europath.tag}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-gray-300 leading-relaxed">{t.divisions.europath.intro}</p>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8">
                <img src={EUROPATH_IMG} alt="Europath" className="w-full h-72 object-cover" loading="lazy" />
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <Link
                to="/contact"
                data-testid="europath-detail-cta"
                className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              >
                {t.common.free_consultation} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {t.divisions.europath.items.map((it, i) => (
                <Reveal key={it} delay={i * 0.04}>
                  <div data-testid={`europath-service-${i}`} className="bg-[#0A192F] p-7 h-full hover:bg-[#0A192F]/70 transition-colors">
                    <Check className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="mt-4 font-serif text-lg sm:text-xl">{it}</h4>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* EUROPATH GALLERY */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Glimpses</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h3 className="mt-3 font-serif text-2xl sm:text-3xl">Education, Language & Vocational Programs</h3>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {EUROPATH_GALLERY.map((img, i) => (
              <Reveal key={img.src} delay={i * 0.05}>
                <figure data-testid={`europath-gallery-${i}`} className="group relative overflow-hidden border border-white/10">
                  <img
                    src={img.src}
                    alt={img.label}
                    loading="lazy"
                    className="w-full h-44 sm:h-56 object-cover transition-transform duration-[1.2s] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/10 to-transparent" />
                  <figcaption className="absolute bottom-3 left-4 right-4 text-[11px] uppercase tracking-[0.25em] text-white">
                    {img.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EUROPATH — BUSINESS SETUP & INVESTMENT */}
      <section className="bg-gray-100 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Europath · For Entrepreneurs</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight leading-tight">
                  {t.divisions.europath.business_overline}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-gray-600 leading-relaxed">{t.divisions.europath.business_intro}</p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-8 relative overflow-hidden">
                  <img src={BUSINESS_IMG} alt="Business setup in Spain" className="w-full h-72 object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Spain · Portugal · EU</p>
                    <p className="mt-1 font-serif text-white text-xl">Turnkey company setup</p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <Link
                  to="/contact"
                  data-testid="europath-business-cta"
                  className="mt-8 inline-flex items-center gap-2 bg-[#0A192F] text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
                >
                  Start Your Business <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
                {t.divisions.europath.business_items.map((it, i) => {
                  const Icon = BUSINESS_ICONS[i % BUSINESS_ICONS.length];
                  return (
                    <Reveal key={it} delay={i * 0.03}>
                      <div data-testid={`europath-business-${i}`} className="bg-white p-6 h-full hover:bg-[#0A192F] hover:text-white transition-colors group">
                        <Icon className="w-6 h-6 text-[#D4AF37]" />
                        <h4 className="mt-4 font-serif text-lg text-[#0A192F] group-hover:text-white leading-snug">{it}</h4>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={0.3}>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                  {[
                    { k: "Restaurants & Bars", v: "Setup · Licenses" },
                    { k: "Hotels", v: "Acquisition · Ops" },
                    { k: "Retail Shops", v: "Lease · Permits" },
                    { k: "Import / Export", v: "Partners · Docs" },
                  ].map((s, i) => (
                    <div key={s.k} data-testid={`biz-highlight-${i}`} className="bg-white p-5">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{s.v}</p>
                      <p className="mt-2 font-serif text-[#0A192F] text-lg leading-tight">{s.k}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;

